import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useInfiniteList } from "../lib/useInfiniteList";
import { Loader2, RefreshCw, Building2, Store, Clock, ArrowUpRight } from "lucide-react";
import { formatGeneratedAt } from "../lib/datetime";

const LAYERS = ["all", "category", "behaviour", "product"];
const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
];

const layerColor = {
  category: "text-bm-cyan",
  behaviour: "text-bm-amber",
  product: "text-bm-green",
};

const layerBorder = {
  category: "border-bm-cyan/40 hover:border-bm-cyan",
  behaviour: "border-bm-amber/40 hover:border-bm-amber",
  product: "border-bm-green/40 hover:border-bm-green",
};

function InsightCard({ insight, idx }) {
  return (
    <Link
      to={`/insights/${insight.id}`}
      aria-label={`Open insight: ${insight.name}`}
      className={`bm-card fade-up p-4 border-l-2 ${layerBorder[insight.layer] || "border-white/20"} flex flex-col gap-3 focus-visible:outline focus-visible:outline-2 focus-visible:outline-bm-cyan hover:-translate-y-0.5`}
      style={{ animationDelay: `${Math.min(idx * 30, 400)}ms` }}
      data-testid={`insight-card-${insight.id}`}
    >
      <div className="flex items-center justify-between">
        <span className={`label-mono ${layerColor[insight.layer] || "text-white"}`}>{insight.layer}</span>
        <span className="flex items-center gap-2 label-mono text-bm-dim">{insight.region}<ArrowUpRight className="w-3 h-3" /></span>
      </div>
      <div>
        <div className="font-heading text-base font-medium text-white leading-tight">{insight.name}</div>
        <p className="text-sm text-bm-muted mt-2 leading-relaxed">{insight.description}</p>
      </div>

      {insight.reference_brands?.length > 0 && (
        <div className="border-t border-white/5 pt-2" data-testid={`insight-brands-${insight.id}`}>
          <div className="flex items-center gap-1 label-mono text-bm-dim mb-1.5">
            <Building2 className="w-3 h-3" />REFERENCE BRANDS
          </div>
          <div className="flex flex-wrap gap-1">
            {insight.reference_brands.slice(0, 4).map((b) => (
              <span key={b} className="text-[11px] font-mono text-bm-amber border border-bm-amber/30 px-1.5 py-0.5">{b}</span>
            ))}
          </div>
        </div>
      )}

      {insight.winning_sellers?.length > 0 && (
        <div data-testid={`insight-sellers-${insight.id}`}>
          <div className="flex items-center gap-1 label-mono text-bm-dim mb-1.5">
            <Store className="w-3 h-3" />WHERE IT IS WINNING
          </div>
          <div className="flex flex-wrap gap-1">
            {insight.winning_sellers.slice(0, 3).map((s) => (
              <span key={s} className="text-[11px] font-mono text-bm-cyan border border-bm-cyan/30 px-1.5 py-0.5">{s}</span>
            ))}
          </div>
        </div>
      )}

      <div className="pt-2 border-t border-white/5 flex items-center gap-1 text-[10px] font-mono text-bm-dim" data-testid={`insight-generated-at-${insight.id}`}>
        <Clock className="w-3 h-3" />
        <span>Generated {formatGeneratedAt(insight.generated_at || insight.created_at)}</span>
      </div>
    </Link>
  );
}

export default function Insights() {
  const [layer, setLayer] = useState("all");
  const [channel, setChannel] = useState("all");
  const [channels, setChannels] = useState([]);
  const [sort, setSort] = useState("newest");
  const [refreshTick, setRefreshTick] = useState(0);

  useEffect(() => {
    api.insightChannels().then((d) => setChannels(d.channels || []));
  }, []);

  const fetchPage = useCallback((skip, limit) => {
    const params = { skip, limit, sort };
    if (layer !== "all") params.layer = layer;
    if (channel !== "all") params.platform = channel;
    return api.insights(params);
  }, [layer, channel, sort, refreshTick]);

  const { items, total, totalReady, hasMore, loading, sentinelRef } = useInfiniteList(fetchPage, `${layer}|${channel}|${sort}|${refreshTick}`, 12);

  return (
    <div className="space-y-5">
      <div className="border-b border-white/10 pb-4">
        <div className="label-mono text-bm-amber">INSIGHTS</div>
        <h1 className="font-heading text-4xl font-black tracking-tight mt-1" data-testid="page-title">
          Why it is changing.
        </h1>
        <p className="text-sm text-bm-muted mt-2 max-w-2xl">
          Structured intelligence — category shifts, behaviour graphs, and product adjacencies mapped across geographies.
        </p>
      </div>

      <div className="bm-card p-3 flex flex-wrap items-center gap-4 sticky top-14 z-20 bg-bm-surface/95 backdrop-blur">
        <div className="flex items-center gap-2">
          <span className="label-mono text-bm-dim">LAYER</span>
          <div className="flex gap-1">
            {LAYERS.map(l => (
              <button key={l} onClick={() => setLayer(l)} data-testid={`filter-layer-${l}`}
                className={`px-2 py-1 text-xs font-mono uppercase border ${layer === l ? "bg-bm-amber text-black border-bm-amber" : "border-white/10 text-bm-muted hover:text-white hover:border-white/30"}`}>
                {l}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-mono text-bm-dim">CHANNEL</span>
          <select value={channel} onChange={(e) => setChannel(e.target.value)} data-testid="filter-channel"
            className="bg-bm-elevated border border-white/10 text-xs font-mono px-2 py-1 text-white outline-none">
            <option value="all">ALL</option>
            {channels.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <span className="label-mono text-bm-dim">SORT</span>
          <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="filter-sort"
            className="bg-bm-elevated border border-white/10 text-xs font-mono px-2 py-1 text-white outline-none">
            {SORTS.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>
        <button onClick={() => setRefreshTick((t) => t + 1)} data-testid="insights-live-btn"
          className="ml-auto flex items-center gap-2 px-3 py-1.5 text-xs font-mono border border-bm-green/40 text-bm-green hover:bg-bm-green hover:text-black transition-colors">
          <span className="w-1.5 h-1.5 bg-bm-green rounded-full animate-pulse"></span>
          <RefreshCw className="w-3 h-3" />LIVE
        </button>
        <div className="label-mono text-bm-green" data-testid="insights-count">{totalReady ? `${items.length} / ${total}` : "…"}</div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3" data-testid="insights-grid">
        {items.map((ins, i) => <InsightCard key={ins.id} insight={ins} idx={i} />)}
      </div>

      {hasMore && (
        <div ref={sentinelRef} className="flex items-center justify-center py-8" data-testid="insights-sentinel">
          <Loader2 className="w-4 h-4 animate-spin text-bm-cyan" />
          <span className="ml-2 font-mono text-xs text-bm-cyan">{loading ? "STRUCTURING NEXT BATCH..." : "SCROLL FOR MORE"}</span>
        </div>
      )}

      {!hasMore && items.length > 0 && (
        <div className="text-center py-8 font-mono text-xs text-bm-dim" data-testid="insights-end">
          — END OF INSIGHTS · {total} MAPPED —
        </div>
      )}
    </div>
  );
}
